import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { createAndObserveIntersection } from '@digilize/shared/utils/helpers/src';
import { TableHeaderTemplateDirective, TableRowTemplateDirective } from './generic-table.directive';
import { Sort } from '@angular/material/sort';
import { Subscription } from 'rxjs';

/** How far below the scrollport the end-of-list sentinel still counts as reached. */
const END_OF_LIST_MARGIN = '0px 0px 200px 0px';

@Component({
  selector: 'lib-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrls: ['./generic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GenericTableComponent<TItem extends object> implements OnInit, AfterViewInit, OnDestroy {
  @Input() rowType: 'collapse' | 'separate' = 'separate';
  // Vertical separators between body cells. 'none' for a table that reads as flat rows.
  @Input() cellBorders: 'solid' | 'none' = 'solid';
  // Horizontal padding on every cell. Any CSS length; the outer edges keep their own insets
  // (--lib-generic-table-padding-left on the first cell, 10px on the last).
  @Input() cellPaddingX = '12px';
  // The table scrolls sideways inside the component instead of the cells being squeezed past
  // readability and, below $width-lg, every row folding into a card.
  @Input() horizontalScroll = false;
  // Any CSS length: the width the columns need to stay readable, below which the container scrolls
  // instead of squeezing them further. Above it nothing changes - the columns share the extra space
  // the way an auto-layout table always has, so a wide screen keeps fully dynamic cells.
  @Input() minTableWidth?: string;
  // Any CSS length. Caps the scrolling area so the table scrolls in both directions inside the
  // viewport rather than the page scrolling: that keeps the horizontal bar at the bottom of the
  // visible area on a table hundreds of rows tall, and lets a consumer's sticky header pin.
  @Input() maxHeight?: string;
  @Input() withAction = true;
  @Input() data!: TItem[];
  @ContentChild(TableHeaderTemplateDirective, { read: TemplateRef })
  headers?: TemplateRef<any>;
  @ContentChild(TableRowTemplateDirective, { read: TemplateRef })
  rows?: TemplateRef<any>;
  @ViewChild('endOfList') endOfList: ElementRef;
  @ViewChild('tableScroll') tableScroll: ElementRef<HTMLElement>;
  @Output() scrolledToEnd = new EventEmitter<boolean>();

  // Drives the scroll-mode rules in the stylesheet; a class rather than a media query in TS so the
  // breakpoint stays in one place.
  @HostBinding('class.horizontal-scroll') get isHorizontalScroll(): boolean {
    return this.horizontalScroll;
  }

  private endOfListSub?: Subscription;

  constructor() {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // With maxHeight the container is what scrolls, so the sentinel has to be measured against it -
    // against the viewport it would sit permanently in view and load every page at once.
    const root = this.maxHeight ? this.tableScroll?.nativeElement : null;
    // The sentinel has no height and ends the scrolled content, so against the container it only
    // ever meets the root's bottom edge and never crosses into it - the margin is what gives it a
    // band to intersect, and it loads the next page just before the floor instead of on it
    // (PAUL-3548). The viewport root keeps the page flow's own slack, so it needs none.
    const rootMargin = root ? END_OF_LIST_MARGIN : undefined;
    this.endOfListSub = createAndObserveIntersection(this.endOfList, root, rootMargin).subscribe((isVisible) => {
      if (isVisible) {
        this.scrolledToEnd.emit(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.endOfListSub?.unsubscribe();
  }

  sortData(sort: Sort) {
    console.log(sort);
  }
}
